import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  bulkActionLabel,
  type BulkFormAction,
} from "@/lib/admin/bulkFormActions";

const controlClass =
  "h-8 rounded-lg border border-slate-200 bg-white font-onest text-xs text-slate-800 shadow-sm transition-colors focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 sm:text-sm";

const selectClass = `${controlClass} cursor-pointer py-0 pl-2.5 pr-8 leading-8`;

interface FormsListBulkBarProps {
  selectedCount: number;
  isRunning: boolean;
  organisationOptions: Array<{ id: number; name: string }>;
  allowEmptyOrganisation?: boolean;
  onAction: (action: BulkFormAction) => void;
  onChangeOrganisation: (organisationId: number | null) => void;
  onClear: () => void;
}

export function FormsListBulkBar({
  selectedCount,
  isRunning,
  organisationOptions,
  allowEmptyOrganisation = false,
  onAction,
  onChangeOrganisation,
  onClear,
}: FormsListBulkBarProps) {
  const [targetOrganisation, setTargetOrganisation] = useState("");

  useEffect(() => {
    if (selectedCount === 0) {
      setTargetOrganisation("");
    }
  }, [selectedCount]);

  if (selectedCount === 0) return null;

  const canApplyOrganisation = targetOrganisation !== "";

  const handleApplyOrganisation = () => {
    if (!canApplyOrganisation) return;
    onChangeOrganisation(
      targetOrganisation === "none" ? null : Number(targetOrganisation),
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-brand-100 bg-brand-50/50 px-3 py-1.5 sm:px-4">
      <span className="font-onest text-xs font-medium text-brand-900">
        Выбрано: {selectedCount}
      </span>
      <span className="hidden h-3 w-px bg-brand-200 sm:inline" />
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="secondary"
          className="px-3 py-1.5 text-xs"
          disabled={isRunning}
          onClick={() => onAction("publish")}
        >
          {bulkActionLabel("publish", selectedCount)}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="px-3 py-1.5 text-xs"
          disabled={isRunning}
          onClick={() => onAction("close")}
        >
          {bulkActionLabel("close", selectedCount)}
        </Button>
        {organisationOptions.length > 0 || allowEmptyOrganisation ? (
          <>
            <select
              aria-label="Организация для выбранных форм"
              value={targetOrganisation}
              disabled={isRunning}
              onChange={(event) => setTargetOrganisation(event.target.value)}
              className={`${selectClass} max-w-[10rem] sm:max-w-[12rem]`}
            >
              <option value="">Организация…</option>
              {allowEmptyOrganisation ? (
                <option value="none">Без организации</option>
              ) : null}
              {organisationOptions.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="secondary"
              className="px-3 py-1.5 text-xs"
              disabled={isRunning || !canApplyOrganisation}
              onClick={handleApplyOrganisation}
            >
              Сменить организацию
            </Button>
          </>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          className="border-red-200 px-3 py-1.5 text-xs text-red-700 hover:border-red-300 hover:bg-red-50"
          disabled={isRunning}
          onClick={() => onAction("delete")}
        >
          {isRunning
            ? "Выполняется…"
            : bulkActionLabel("delete", selectedCount)}
        </Button>
      </div>
      <button
        type="button"
        disabled={isRunning}
        onClick={onClear}
        className="ml-auto font-onest text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
      >
        Снять выбор
      </button>
    </div>
  );
}
